import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, output } from '@angular/core';
import { ButtonComponent } from '../../forms/button/button.component';
import { InlineSpinnerComponent } from '../../spinner/inline-spinner.component';

/**
 * Sticky footer that saves or discards a settings form. It is always
 * rendered with a reserved status line, so nothing moves when the form
 * becomes dirty, starts saving or fails. Place it as the last child of the
 * page inside the scrolling container.
 */
@Component({
  selector: 'app-save-bar-cabinet',
  templateUrl: './save-bar.component.html',
  styleUrl: './save-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [ButtonComponent, InlineSpinnerComponent],
})
export class SaveBarCabinetComponent {
  readonly dirty = input(false);
  readonly saving = input(false);
  readonly error = input<string | null>(null);
  /** False while the form is invalid; Save stays disabled and the status says why. */
  readonly canSave = input(true);
  readonly invalidMessage = input('Some settings have invalid values.');
  readonly saveLabel = input('Save changes');
  readonly discardLabel = input('Discard');

  readonly save = output<void>();
  readonly discard = output<void>();

  readonly status = computed(() => {
    if (this.saving()) return 'Saving changes…';
    const error = this.error();
    if (error) return error;
    if (!this.dirty()) return 'All changes saved';
    return this.canSave() ? 'Unsaved changes' : this.invalidMessage();
  });

  readonly saveDisabled = computed(() => !this.dirty() || !this.canSave());

  onSave(): void {
    if (this.saving() || this.saveDisabled()) return;
    this.save.emit();
  }

  onDiscard(): void {
    if (this.saving() || !this.dirty()) return;
    this.discard.emit();
  }
}
